import { ArrowRight, UserRoundCog, CheckCircle2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useProfile } from '@/hooks/useProfile';
import { useOnboarding } from '@/hooks/useOnboarding';
import { useOnboardingReplay } from '@/hooks/useOnboardingReplay';
import { ACQUISITION_OPTIONS } from '@/lib/onboarding';

/**
 * Dashboard card tied to the "how did you get to FOCO?" segmentation question
 * (profiles.acquisition_source):
 *  - If not answered → a reminder with a CTA to complete it.
 *  - If already answered → shows the saved answer with an option to change it.
 * Both actions relaunch the full onboarding flow (segmentation → tour/activation
 * → WOW) so the user experiences the same branch as a first-time user. Hidden
 * while the guided onboarding flow is still running.
 */
export function UserInfoReminder() {
  const { isLoading } = useProfile();
  const { needsOnboarding, acquisitionSource } = useOnboarding();
  const { startReplay } = useOnboardingReplay();

  // Hide while the profile loads or the guided onboarding flow is still running.
  if (isLoading || needsOnboarding) return null;

  const selectedOption = acquisitionSource
    ? ACQUISITION_OPTIONS.find((o) => o.value === acquisitionSource) ?? null
    : null;

  if (selectedOption) {
    // Answered state — compact summary with a "change" action that replays the flow.
    return (
      <div className="animate-fade-in flex items-center gap-3 rounded-xl border bg-card p-3.5 shadow-sm">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-success/10">
          <CheckCircle2 className="h-5 w-5 text-success" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">Cómo llegaste a FOCO</p>
          <p className="truncate text-sm font-medium">
            <span className="mr-1" aria-hidden>{selectedOption.emoji}</span>
            {selectedOption.label}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 gap-1.5 text-muted-foreground"
          onClick={startReplay}
        >
          <Pencil className="h-3.5 w-3.5" />
          Cambiar
        </Button>
      </div>
    );
  }

  // Unanswered state — prominent reminder that launches the full flow.
  return (
    <div className="animate-slide-up relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-accent/5 p-5 shadow-sm">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary to-accent" />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="mx-auto flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10 sm:mx-0">
          <UserRoundCog className="h-7 w-7 text-primary" />
        </div>

        <div className="min-w-0 flex-1 space-y-1 text-center sm:text-left">
          <p className="text-base font-semibold sm:text-lg">Completa tu información</p>
          <p className="text-xs text-muted-foreground sm:text-sm">
            Cuéntanos cómo llegaste a FOCO —si vienes de la mentoría, de redes sociales o por
            recomendación— para personalizar tu experiencia. Toma 5 segundos.
          </p>
        </div>

        <Button className="w-full shrink-0 gap-2 shadow-md sm:w-auto" onClick={startReplay}>
          Completar
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
